import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { MagnifyingGlass, Buildings, FileText, ChartBar, Receipt, FileArrowUp, PencilSimple } from "@phosphor-icons/react";
import PesquisaExternaForm from "./PesquisaExternaForm";

const fmtBRL = (v) => (v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 2 });
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("pt-BR") : "—";

function StatusBadge({ pesq }) {
  // Heurística: pesquisa finalizada quando tem itens analisados e preço-referência
  const finalizada = (pesq?.estatisticas?.qtd || 0) > 0 && (pesq?.estatisticas?.preco_referencia || 0) > 0;
  return finalizada ? (
    <span data-testid="badge-status-fin" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-medium px-2 py-1 rounded bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/30">
      Finalizada
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-medium px-2 py-1 rounded bg-[#F59E0B]/15 text-[#F59E0B] border border-[#F59E0B]/30">
      Em andamento
    </span>
  );
}

function ModoBadge({ modo }) {
  if (!modo) return null;
  const isManual = modo === "manual";
  return (
    <span className={`text-[10px] uppercase tracking-wide font-medium px-2 py-0.5 rounded border ${isManual ? "bg-[#3B82F6]/10 text-[#60A5FA] border-[#3B82F6]/30" : "bg-[#94A3B8]/10 text-[#94A3B8] border-[#94A3B8]/30"}`}>
      {isManual ? "Curadoria manual" : "Automática"}
    </span>
  );
}

export default function Pesquisas() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [busca, setBusca] = useState("");
  const [loading, setLoading] = useState(true);
  const [showExternForm, setShowExternForm] = useState(false);

  const load = () => {
    setLoading(true);
    const params = {};
    if (busca && busca.trim().length >= 2) params.termo = busca.trim();
    api.get("/pesquisas-precos", { params })
      .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [busca]);

  // KPIs agregados
  const totalItens = items.reduce((s, p) => s + (p.qtd_itens || 0), 0);
  const totalRef = items.reduce((s, p) => s + ((p.estatisticas?.preco_referencia) || 0), 0);
  const totalFinalizadas = items.filter((p) => (p.estatisticas?.qtd || 0) > 0).length;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="uppercase-label">Lei 14.133/2021 · IN SEGES/ME 65/2021</div>
          <h1 className="font-heading text-4xl tracking-tight mt-2">Pesquisas de Preços</h1>
          <p className="text-sm text-[#94A3B8] mt-2 max-w-2xl">
            Painel de pesquisas realizadas. Consulta unificada de preços-referência por
            contrato, com integração ao PNCP, Compras.gov.br e TCE-CE.
          </p>
        </div>
        <button
          data-testid="btn-importar-externa"
          onClick={() => setShowExternForm(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-[#1E3A8A] text-white hover:bg-[#3B82F6] text-sm font-medium"
        >
          <FileArrowUp size={16} weight="duotone" />
          Importar pesquisa externa
        </button>
      </div>

      {showExternForm && (
        <PesquisaExternaForm
          onClose={() => setShowExternForm(false)}
          onSaved={() => load()}
        />
      )}

      {/* KPI cards estilo M2A */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div data-testid="kpi-total-pesquisas" className="tce-card p-5">
          <div className="flex items-start justify-between">
            <div>
              <div className="uppercase-label">Pesquisas</div>
              <div className="text-3xl font-mono mt-2 text-[#FCD34D]">{total}</div>
            </div>
            <ChartBar size={28} weight="duotone" className="text-[#F59E0B]/70" />
          </div>
        </div>
        <div className="tce-card p-5">
          <div className="flex items-start justify-between">
            <div>
              <div className="uppercase-label">Finalizadas</div>
              <div className="text-3xl font-mono mt-2 text-[#10B981]">{totalFinalizadas}</div>
            </div>
            <Receipt size={28} weight="duotone" className="text-[#10B981]/70" />
          </div>
        </div>
        <div className="tce-card p-5">
          <div className="uppercase-label">Itens encontrados</div>
          <div className="text-3xl font-mono mt-2 text-[#E9EEF5]">{totalItens.toLocaleString("pt-BR")}</div>
        </div>
        <div className="tce-card p-5">
          <div className="uppercase-label">Soma preços-referência</div>
          <div className="text-2xl font-mono mt-2 text-[#FCD34D]">{fmtBRL(totalRef)}</div>
        </div>
      </div>

      {/* Filtro */}
      <div className="tce-card p-5">
        <div className="relative max-w-xl">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <input
            data-testid="filtro-pesquisas"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Digite o termo da pesquisa ou número do contrato..."
            className="w-full pl-9 pr-3 py-2.5 bg-[#0B1220] border border-[#334155] rounded-md text-sm text-[#E9EEF5] placeholder:text-[#6B7280] focus:outline-none focus:border-[#F59E0B]/60"
          />
        </div>
      </div>

      {/* Tabela */}
      <div className="tce-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0B1220] border-b border-[#334155]">
              <tr className="text-left">
                <th className="px-5 py-4 uppercase-label">#</th>
                <th className="px-5 py-4 uppercase-label">Termo</th>
                <th className="px-5 py-4 uppercase-label">Contrato / Item</th>
                <th className="px-5 py-4 uppercase-label">Município</th>
                <th className="px-5 py-4 uppercase-label">Período</th>
                <th className="px-5 py-4 uppercase-label">Modo</th>
                <th className="px-5 py-4 uppercase-label text-right">Itens</th>
                <th className="px-5 py-4 uppercase-label text-right">V. Referência</th>
                <th className="px-5 py-4 uppercase-label text-center">Status</th>
                <th className="px-5 py-4 uppercase-label text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={10} className="px-5 py-12 text-center text-[#94A3B8]">Carregando...</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={10} className="px-5 py-12 text-center text-[#94A3B8]">
                  Nenhuma pesquisa encontrada. Abra um contrato e clique em <strong className="text-[#F59E0B]">Pesquisa de Preços</strong>.
                </td></tr>
              )}
              {items.map((p, i) => (
                <tr key={p.id} data-testid={`pesquisa-row-${p.id}`} className="border-t border-[#334155] hover:bg-[#0B1220]/60">
                  <td className="px-5 py-4 font-mono text-xs text-[#94A3B8]">{i + 1}</td>
                  <td className="px-5 py-4">
                    <div className="text-[#E9EEF5] font-medium">{p.termo || "—"}</div>
                    <div className="text-[10px] text-[#94A3B8] mt-0.5">{fmtDate(p.created_at)}</div>
                  </td>
                  <td className="px-5 py-4">
                    <Link to={`/contratos/${p.contract_id}`} className="text-[#F59E0B] hover:underline" data-testid={`link-contrato-${p.id}`}>
                      {p.contract_numero || "—"}
                    </Link>
                    {p.contract_item_numero ? (
                      <div className="text-[10px] text-[#94A3B8] mt-0.5">Item nº {p.contract_item_numero}</div>
                    ) : null}
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-1.5 text-xs text-[#94A3B8]">
                      <Buildings size={12} /> {p.municipio_nome || "—"}
                    </div>
                  </td>
                  <td className="px-5 py-4 text-xs text-[#94A3B8]">
                    {p.data_inicio || "—"}<br />
                    <span className="text-[10px]">até {p.data_fim || "—"}</span>
                  </td>
                  <td className="px-5 py-4"><ModoBadge modo={p.modo_curadoria} /></td>
                  <td className="px-5 py-4 text-right font-mono">{p.qtd_itens || 0}</td>
                  <td className="px-5 py-4 text-right font-mono text-[#FCD34D]">
                    {fmtBRL(p.estatisticas?.preco_referencia)}
                  </td>
                  <td className="px-5 py-4 text-center"><StatusBadge pesq={p} /></td>
                  <td className="px-5 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`/editor/pesquisa/${p.id}`}
                        data-testid={`btn-editor-${p.id}`}
                        title="Abrir Editor Inteligente da conclusão"
                        className="inline-flex items-center gap-1 text-[#8B5CF6] hover:text-[#A78BFA] text-xs font-medium"
                      >
                        <PencilSimple size={12} weight="duotone" /> Editor
                      </Link>
                      <Link
                        to={`/pesquisas/${p.id}`}
                        data-testid={`btn-abrir-${p.id}`}
                        className="inline-flex items-center gap-1 text-[#F59E0B] hover:text-[#FCD34D] text-xs font-medium"
                      >
                        <FileText size={12} weight="duotone" /> Abrir
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
